'use client'

import { useState } from 'react'

import dynamic from 'next/dynamic'

import { ImageWrapper } from '@/components/image'
import { useTranslation } from '@/hooks/client/use-translation'
import { tw } from '@/lib/utils'

import type { PriceSellerData } from './price-section-types'

const SellerInfoModal = dynamic(() => import('./seller-info-modal').then((module) => module.SellerInfoModal), {
  ssr: false,
})

interface SellerIdentityProps {
  seller: PriceSellerData
  featured: boolean
  lowestPrice: boolean
  className?: string
}

const StarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 23 22" fill="none" aria-hidden="true" className="shrink-0">
    <path
      d="M11.164 16.549l4.851-1.23 2.027 6.247-6.878-5.017zm11.164-8.074h-8.539l-2.625-8.04-2.625 8.04H0l6.911 4.984-2.625 8.04 6.911-4.983 4.253-3.057 6.878-4.984z"
      fill="#219653"
    />
  </svg>
)

/** The seller row's identity block: logo, the info button, tags, then the meta and trust lines. */
export const SellerIdentity = ({ seller, featured, lowestPrice, className }: SellerIdentityProps) => {
  const { t, locale } = useTranslation()
  const [infoOpen, setInfoOpen] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const rating = seller.sellerRating != null && seller.sellerRating > 0 ? seller.sellerRating : null
  const reviews = seller.sellerReviews != null && seller.sellerReviews > 0 ? seller.sellerReviews : null
  const tagline = seller.sellerTagline || seller.care?.type
  const hasMeta = Boolean(tagline) || rating != null

  return (
    <div className={tw('min-w-0', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h3 className="text-navy m-0 text-base leading-tight font-bold">
          {seller.logoUrl && !logoFailed ? (
            <ImageWrapper
              image={seller.logoUrl}
              origin="product-prices-seller-logo"
              loadTime="lazy"
              sizingProps={{ minWidth: 130, maxWidth: 260, resolutionCount: 2, sizes: '130px', quality: '80' }}
              imgProps={{
                alt: seller.sellerName,
                className: 'h-[26px] w-auto max-w-[130px] object-contain',
                onError: () => setLogoFailed(true),
              }}
            />
          ) : (
            seller.sellerName
          )}
        </h3>
        <button
          type="button"
          onClick={() => setInfoOpen(true)}
          aria-haspopup="dialog"
          aria-label={t('product.aboutSeller', 'About {{seller}}', { seller: seller.sellerName })}
          data-tracking="ignore"
          className="text-navy-60 hover:text-navy flex h-8 w-6 shrink-0 cursor-pointer items-center justify-center rounded-sm text-[11px] leading-none font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis"
        >
          <span
            aria-hidden="true"
            className="flex h-[17px] w-[17px] items-center justify-center rounded-full border-[1.5px] border-current"
          >
            i
          </span>
        </button>
        {(featured || seller.sponsored) && (
          <span className="bg-lapis/10 text-lapis rounded-full px-2.5 py-px text-[11px] font-bold">
            {seller.sponsored ? t('product.purchase.sponsored', 'Sponsored') : t('product.featured', 'Featured')}
          </span>
        )}
        {lowestPrice && (
          <span className="rounded-full bg-[rgba(23,159,45,0.1)] px-2.5 py-px text-[11px] font-bold text-[#12803f]">
            {t('product.sellers.lowestPrice', 'Lowest price')}
          </span>
        )}
      </div>

      {hasMeta && (
        <div className="mt-1.5 overflow-hidden">
          <p className="text-navy-60 -ml-2 flex min-w-0 flex-wrap items-center gap-y-2 text-[13.5px] leading-tight">
            {tagline && <span className="px-2 text-navy font-semibold">{tagline}</span>}
            {rating != null && (
              <span
                className={tw(
                  'relative inline-flex items-center gap-1 px-2',
                  tagline &&
                    'before:absolute before:top-1/2 before:left-0 before:h-3 before:w-px before:-translate-y-1/2 before:bg-navy-20'
                )}
              >
                <StarIcon />
                <span className="text-navy font-bold">{rating.toLocaleString(locale)}</span>
                {reviews != null && (
                  <span className="text-navy-60">
                    {t('product.sellers.reviewCount', '({{count}} reviews)', { count: reviews.toLocaleString(locale) })}
                  </span>
                )}
              </span>
            )}
          </p>
        </div>
      )}

      {seller.sellerFeatures.length > 0 && (
        <div className="mt-[5px] overflow-hidden">
          <ul className="-ml-2 flex list-none flex-wrap gap-y-0.5 p-0 text-[13px] leading-snug text-navy-60">
            {seller.sellerFeatures.map((feature) => (
              <li
                key={feature}
                className="relative max-w-full px-2 before:absolute before:top-[0.3em] before:left-0 before:h-2.5 before:w-px before:bg-navy-20"
              >
                {feature}
              </li>
            ))}
          </ul>
        </div>
      )}

      {infoOpen && <SellerInfoModal seller={seller} onClose={() => setInfoOpen(false)} />}
    </div>
  )
}
